import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getGestiones, getPendientes, saveGestionesCache, loadGestionesCache, refreshToken, login, cambiarPassword } from '../utils/api';

import SesionView from './SesionView';
import SearchPage from './SearchPage';
import AdminPage from './AdminPage';
import PendientesPage from './PendientesPage';

// Texto contra el que se compara la búsqueda — debe incluir el mismo formato
// que se muestra en pantalla ("Sesión 01/2026", "Ext. 03/2026"), si no el
// usuario escribe justo lo que ve y no encuentra nada.
function sessionSearchLabel(s, g) {
  const numLabel = String(s.numero).padStart(2, '0');
  const tipoLabel = s.tipo === 'ordinaria' ? `Sesión ${numLabel}` : `Ext. ${numLabel}`;
  return `${tipoLabel} ${g.anio_inicio} ${s.tipo} ${s.numero} ${g.anio_inicio}-${g.anio_fin} ${s.fecha_hora || ''} ${s.descripcion || ''}`.toLowerCase();
}

// Tono corto generado con Web Audio API — sin depender de un archivo de audio.
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = now + i * 0.12;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.15, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.3);
    });
  } catch {}
}

export default function MainApp({ user, onLogout }) {
  const [gestiones, setGestiones] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [expandedSesiones, setExpandedSesiones] = useState({}); // { gestionId_tipo: true } para mostrar todas
  const [selectedSesion, setSelectedSesion] = useState(null);
  const [view, setView] = useState('sesion'); // sesion | search | admin | pendientes
  const [search, setSearch] = useState('');
  const [pendientesCount, setPendientesCount] = useState(0);
  const [updateVersion, setUpdateVersion] = useState(null);
  const [online, setOnline] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificacion, setNotificacion] = useState(null);
  const sesionConocidaRef = useRef(undefined); // undefined = todavía no se cargó nada
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordOk, setPasswordOk] = useState('');

  const abrirCambiarPassword = () => {
    setPasswordForm({ actual: '', nueva: '', confirmar: '' });
    setPasswordError(''); setPasswordOk('');
    setShowPasswordModal(true);
    setUserMenuOpen(false);
  };

  const handleCambiarPassword = async () => {
    setPasswordError(''); setPasswordOk('');
    if (!passwordForm.actual || !passwordForm.nueva) return setPasswordError('Complete ambos campos');
    if (passwordForm.nueva !== passwordForm.confirmar) return setPasswordError('La confirmación no coincide');
    if (passwordForm.nueva.length < 4) return setPasswordError('La contraseña nueva es muy corta');
    try {
      await cambiarPassword(passwordForm.actual, passwordForm.nueva);
      setPasswordOk('Contraseña actualizada correctamente');
      setTimeout(() => setShowPasswordModal(false), 1200);
    } catch (e) {
      setPasswordError(e.response?.data?.error || 'Error al cambiar la contraseña');
    }
  };

  const loadPendientesCount = async () => {
    try {
      const data = await getPendientes();
      setPendientesCount(data.length);
    } catch {}
  };

  const loadGestiones = async (isPolling = false) => {
    try {
      const data = await getGestiones();
      setGestiones(data);
      setOnline(true);
      setLastSync(new Date());
      // Guardar en caché para uso offline
      saveGestionesCache(data);
      setExpanded(prev => {
        if (Object.keys(prev).length === 0 && data.length > 0) {
          return { [data[0].id]: true };
        }
        if (isPolling && data.length > 0) {
          const nuevas = data.filter(g => !(g.id in prev));
          if (nuevas.length > 0) {
            const extra = {};
            nuevas.forEach(g => { extra[g.id] = true; });
            return { ...prev, ...extra };
          }
        }
        return prev;
      });
    } catch {
      // Sin conexión — cargar desde caché
      setOnline(false);
      if (!isPolling) {
        const cached = loadGestionesCache();
        if (cached) {
          setGestiones(cached);
          setExpanded(prev => {
            if (Object.keys(prev).length === 0 && cached.length > 0) {
              return { [cached[0].id]: true };
            }
            return prev;
          });
        }
      }
    }
  };

  useEffect(() => {
    if (window.electronAPI?.onUpdateReady) {
      window.electronAPI.onUpdateReady((version) => setUpdateVersion(version));
    }

    // Renovar/restaurar sesión al abrir
    const renovarSesion = async () => {
      try {
        const data = await refreshToken();
        localStorage.setItem('token', data.token);
      } catch {
        try {
          if (window.electronAPI) {
            const creds = await window.electronAPI.getCredentials();
            if (creds.usuario && creds.password) {
              const data = await login(creds.usuario, creds.password);
              localStorage.setItem('token', data.token);
              localStorage.setItem('user', JSON.stringify(data.usuario));
            }
          }
        } catch {}
      }
    };

    renovarSesion().then(() => {
      loadGestiones();
      loadPendientesCount();
    });

    // Polling datos cada 30 segundos
    const interval = setInterval(() => { loadGestiones(true); loadPendientesCount(); }, 30000);

    // Renovar token cada 7 horas
    const tokenInterval = setInterval(async () => {
      try {
        const data = await refreshToken();
        localStorage.setItem('token', data.token);
      } catch {
        try {
          if (window.electronAPI) {
            const creds = await window.electronAPI.getCredentials();
            if (creds.usuario && creds.password) {
              const data = await login(creds.usuario, creds.password);
              localStorage.setItem('token', data.token);
            }
          }
        } catch {}
      }
    }, 7 * 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearInterval(tokenInterval);
    };
  }, []);

  // Auto-expandir solo cuando el usuario escribe en el buscador
  useEffect(() => {
    if (!search.trim()) return;
    setExpanded(prev => {
      const next = { ...prev };
      gestiones.forEach(g => {
        const tiene = g.sesiones?.some(s => sessionSearchLabel(s, g).includes(search.toLowerCase()));
        if (tiene) next[g.id] = true;
      });
      return next;
    });
  }, [search]);

  const toggleYear = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const initials = (nombre) => nombre.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

  const gestionLabel = (g) => `Gestión ${g.anio_inicio}-${g.anio_fin}`;

  const filteredGestiones = gestiones.map(g => {
    const sesiones = g.sesiones?.filter(s => {
      if (!search) return true;
      return sessionSearchLabel(s, g).includes(search.toLowerCase());
    });
    return { ...g, sesiones };
  });

  // Sesión creada más recientemente (mayor id = más nueva), para resaltarla en el árbol
  const sesionMasRecienteId = useMemo(() => {
    let masReciente = null;
    gestiones.forEach(g => {
      g.sesiones?.forEach(s => {
        if (!masReciente || s.id > masReciente) masReciente = s.id;
      });
    });
    return masReciente;
  }, [gestiones]);

  // Se resalta solo mientras la fecha/hora fijada de la sesión no haya pasado
  // (si ya pasó, ya no tiene sentido llamar la atención sobre ella)
  const esSesionDestacada = (s) =>
    s.id === sesionMasRecienteId && (!s.fecha_hora || new Date(s.fecha_hora) > new Date());

  // Cuando el polling detecta una sesión más nueva que la última conocida,
  // se abre sola, se avisa con un cartel y un tono.
  const buscarSesion = (id) => {
    for (const g of gestiones) {
      const s = g.sesiones?.find(s => s.id === id);
      if (s) return { sesion: s, gestion: g };
    }
    return null;
  };

  useEffect(() => {
    if (sesionMasRecienteId == null) return;

    if (sesionConocidaRef.current === undefined) {
      // Primera carga de la app: seleccionarla directamente, sin cartel ni sonido
      sesionConocidaRef.current = sesionMasRecienteId;
      const encontrada = buscarSesion(sesionMasRecienteId);
      if (encontrada) {
        setSelectedSesion({ ...encontrada.sesion, gestion: encontrada.gestion });
        setView('sesion');
      }
      return;
    }
    if (sesionMasRecienteId === sesionConocidaRef.current) return;
    if (sesionMasRecienteId < sesionConocidaRef.current) {
      sesionConocidaRef.current = sesionMasRecienteId; // se eliminó la más reciente — no avisar
      return;
    }
    sesionConocidaRef.current = sesionMasRecienteId;

    const encontrada = buscarSesion(sesionMasRecienteId);
    if (!encontrada) return;

    setSelectedSesion({ ...encontrada.sesion, gestion: encontrada.gestion });
    setView('sesion');
    const label = `${encontrada.sesion.tipo === 'ordinaria' ? 'Sesión' : 'Ext.'} ${String(encontrada.sesion.numero).padStart(2, '0')}/${encontrada.gestion.anio_inicio}`;
    setNotificacion(`🔔 Nueva sesión publicada: ${label}`);
    playChime();
    setTimeout(() => setNotificacion(null), 8000);
  }, [sesionMasRecienteId, gestiones]);



  return (
    <div className="app-layout">
      <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Abrir menú">☰</button>
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      {notificacion && (
        <div className="toast-nueva-sesion" onClick={() => setNotificacion(null)}>
          {notificacion}
        </div>
      )}

      {/* ── SIDEBAR ── */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">AL</div>
            <div className="sidebar-brand-text">Asamblea Legislativa<br/>Dpto. de Potosí</div>
          </div>
          <div className="sidebar-search">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.4 }}>
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              placeholder="Buscar sesión..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="sidebar-tree">
          {/* Opción búsqueda global */}
          <div
            className={`session-item ${view === 'search' ? 'active' : ''}`}
            style={{ paddingLeft: 8, marginBottom: 4 }}
            onClick={() => { setView('search'); setSelectedSesion(null); setSidebarOpen(false); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <span>Búsqueda global</span>
          </div>

          <div
            className={`session-item ${view === 'pendientes' ? 'active' : ''}`}
            style={{ paddingLeft: 8, marginBottom: 4, position: 'relative' }}
            onClick={() => { setView('pendientes'); setSelectedSesion(null); setSidebarOpen(false); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
            <span>Bandeja de pendientes</span>
            {pendientesCount > 0 && (
              <span style={{
                marginLeft: 'auto',
                background: '#c8102e',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                minWidth: 18,
                height: 18,
                borderRadius: 9,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 5px',
                animation: view !== 'pendientes' ? 'pulse-badge 2s ease-in-out infinite' : 'none',
                boxShadow: view !== 'pendientes' ? '0 0 0 3px rgba(200,16,46,0.3)' : 'none'
              }}>
                {pendientesCount}
              </span>
            )}
          </div>

          {user.rol === 'admin' && (
            <div
              className={`session-item ${view === 'admin' ? 'active' : ''}`}
              style={{ paddingLeft: 8, marginBottom: 8 }}
              onClick={() => { setView('admin'); setSelectedSesion(null); setSidebarOpen(false); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span>Administrar</span>
            </div>
          )}

          {/* Árbol de gestiones */}
          {filteredGestiones.map(g => (
            <div key={g.id} className="year-block">
              <div className="year-header" onClick={() => toggleYear(g.id)}>
                <span style={{ opacity: 0.6, fontSize: 12 }}>📁</span>
                <span>Gestión {g.anio_inicio}-{g.anio_fin}</span>
                <span className={`year-arrow ${expanded[g.id] ? 'open' : ''}`}>▶</span>
                <span className="year-count">{g.sesiones?.length || 0}</span>
              </div>

              {expanded[g.id] && (
                <div>
                  {/* Ordinarias */}
                  {(() => {
                    const ords = g.sesiones?.filter(s => s.tipo === 'ordinaria') || [];
                    const key = `${g.id}_ord`;
                    const mostrarTodas = expandedSesiones[key];
                    const visibles = mostrarTodas ? ords : ords.slice(-5);
                    if (!ords.length) return null;
                    return (
                      <>
                        <div className="session-group-label">Ordinarias</div>
                        {!mostrarTodas && ords.length > 5 && (
                          <div
                            onClick={() => setExpandedSesiones(e => ({ ...e, [key]: true }))}
                            style={{ padding:'4px 8px 4px 20px', fontSize:11, color:'rgba(255,255,255,0.35)', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}
                          >
                            <span>▸</span>
                            <span>{ords.length - 5} sesiones anteriores...</span>
                          </div>
                        )}
                        {visibles.map(s => (
                          <div
                            key={s.id}
                            className={`session-item ${selectedSesion?.id === s.id ? 'active' : ''}`}
                            onClick={() => { setSelectedSesion({ ...s, gestion: g }); setView('sesion'); setSidebarOpen(false); }}
                          >
                            <span className="session-dot dot-ord"></span>
                            <span className={s.id === sesionMasRecienteId ? 'session-label-nueva' : ''}>Sesión {String(s.numero).padStart(2,'0')}/{g.anio_inicio}</span>
                            {esSesionDestacada(s) && <span className="dot-nueva" title="Sesión más reciente"></span>}
                            {s.estado_plazo === 'cerrado' && <span title="Plazo cerrado" style={{fontSize:10,marginLeft:'auto',marginRight:4}}>⏰</span>}
                            {s.estado_plazo === 'realizada' && <span title="Sesión realizada" style={{fontSize:10,marginLeft:'auto',marginRight:4,opacity:0.5}}>✓</span>}
                            <span className="session-item-count">{s.total_docs}</span>
                          </div>
                        ))}
                        {mostrarTodas && ords.length > 5 && (
                          <div
                            onClick={() => setExpandedSesiones(e => ({ ...e, [key]: false }))}
                            style={{ padding:'4px 8px 4px 20px', fontSize:11, color:'rgba(255,255,255,0.35)', cursor:'pointer' }}
                          >
                            ▴ Mostrar menos
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {/* Extraordinarias */}
                  {(() => {
                    const exts = g.sesiones?.filter(s => s.tipo === 'extraordinaria') || [];
                    const key = `${g.id}_ext`;
                    const mostrarTodas = expandedSesiones[key];
                    const visibles = mostrarTodas ? exts : exts.slice(-5);
                    if (!exts.length) return null;
                    return (
                      <>
                        <div className="session-group-label">Extraordinarias</div>
                        {!mostrarTodas && exts.length > 5 && (
                          <div
                            onClick={() => setExpandedSesiones(e => ({ ...e, [key]: true }))}
                            style={{ padding:'4px 8px 4px 20px', fontSize:11, color:'rgba(255,255,255,0.35)', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}
                          >
                            <span>▸</span>
                            <span>{exts.length - 5} sesiones anteriores...</span>
                          </div>
                        )}
                        {visibles.map(s => (
                          <div
                            key={s.id}
                            className={`session-item ${selectedSesion?.id === s.id ? 'active' : ''}`}
                            onClick={() => { setSelectedSesion({ ...s, gestion: g }); setView('sesion'); setSidebarOpen(false); }}
                          >
                            <span className="session-dot dot-ext"></span>
                            <span className={s.id === sesionMasRecienteId ? 'session-label-nueva' : ''}>Ext. {String(s.numero).padStart(2,'0')}/{g.anio_inicio}</span>
                            {esSesionDestacada(s) && <span className="dot-nueva" title="Sesión más reciente"></span>}
                            {s.estado_plazo === 'cerrado' && <span title="Plazo cerrado" style={{fontSize:10,marginLeft:'auto',marginRight:4}}>⏰</span>}
                            {s.estado_plazo === 'realizada' && <span title="Sesión realizada" style={{fontSize:10,marginLeft:'auto',marginRight:4,opacity:0.5}}>✓</span>}
                            <span className="session-item-count">{s.total_docs}</span>
                          </div>
                        ))}
                        {mostrarTodas && exts.length > 5 && (
                          <div
                            onClick={() => setExpandedSesiones(e => ({ ...e, [key]: false }))}
                            style={{ padding:'4px 8px 4px 20px', fontSize:11, color:'rgba(255,255,255,0.35)', cursor:'pointer' }}
                          >
                            ▴ Mostrar menos
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {g.sesiones?.length === 0 && (
                    <div style={{ padding: '6px 8px 6px 20px', fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>
                      Sin sesiones
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="user-info user-info-clickable" onClick={() => setUserMenuOpen(o => !o)}>
            <div className="user-avatar">{initials(user.nombre)}</div>
            <div style={{ flex: 1 }}>
              <div className="user-name">{user.nombre}</div>
              <div className="user-rol">{user.rol === 'admin' ? 'Administrador' : 'Asambleísta'}</div>
            </div>
            <span className={`user-menu-arrow ${userMenuOpen ? 'open' : ''}`}>▾</span>
          </div>
          <div className={`user-menu ${userMenuOpen ? 'open' : ''}`}>
            <button className="btn-user-menu" onClick={abrirCambiarPassword}>Cambiar contraseña</button>
            <button className="btn-user-menu danger" onClick={onLogout}>Cerrar sesión</button>
          </div>
        </div>
      </aside>

      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cambiar contraseña</h3>
              <button className="btn-icon" onClick={() => setShowPasswordModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {passwordError && <div className="alert alert-err">{passwordError}</div>}
              {passwordOk && <div className="alert alert-ok">{passwordOk}</div>}
              <div className="form-group">
                <label>Contraseña actual</label>
                <input type="password" value={passwordForm.actual} onChange={e => setPasswordForm(f => ({ ...f, actual: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Contraseña nueva</label>
                <input type="password" value={passwordForm.nueva} onChange={e => setPasswordForm(f => ({ ...f, nueva: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Confirmar contraseña nueva</label>
                <input type="password" value={passwordForm.confirmar} onChange={e => setPasswordForm(f => ({ ...f, confirmar: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-action btn-outline" onClick={() => setShowPasswordModal(false)}>Cancelar</button>
              <button className="btn-action btn-red" onClick={handleCambiarPassword}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN ── */}
      <div className="main-panel">
        {view === 'sesion' && (
          <SesionView
            sesion={selectedSesion}
            user={user}
            onRefresh={loadGestiones}
          />
        )}
        {view === 'search' && <SearchPage gestiones={gestiones} user={user} />}
        {view === 'pendientes' && (
          <PendientesPage user={user} gestiones={gestiones} onRefresh={loadPendientesCount} />
        )}
        {view === 'admin' && user.rol === 'admin' && (
          <AdminPage gestiones={gestiones} onRefresh={loadGestiones} />
        )}

        {/* Indicador de actualización — discreto, en la barra inferior */}
        {updateVersion && (
          <div style={{ padding:'6px 20px', background:'#f0fdf4', borderTop:'1px solid #bbf7d0', display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#166534' }}>
            <span>✓</span>
            <span>Versión {updateVersion} descargada — se instalará automáticamente al cerrar la aplicación.</span>
          </div>
        )}
        <div className="statusbar">
          {online ? (
            <span className="status-online">● Conectado</span>
          ) : (
            <span style={{ color: '#f87171' }}>● Sin conexión — mostrando datos guardados</span>
          )}
          <span>·</span>
          <span>{user.nombre} — {user.rol === 'admin' ? 'Administrador' : 'Asambleísta'}</span>
          <span>·</span>
          <span>Gestiones: {gestiones.length}</span>
          {lastSync && online && (
            <>
              <span>·</span>
              <span>Actualizado: {lastSync.toLocaleTimeString('es-BO', { hour:'2-digit', minute:'2-digit' })}</span>
            </>
          )}
          {!online && lastSync && (
            <>
              <span>·</span>
              <span style={{ color: '#f87171' }}>
                Última sync: {lastSync.toLocaleTimeString('es-BO', { hour:'2-digit', minute:'2-digit' })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
